/**
 * tx.tsx — the transaction cells shared by the home card, the cash-flow table and the expenses
 * table: the editable category button, the invoice state chip, the receipt-match chip and the
 * mobile list row.
 *
 * spec-business §4.2/§4.5: the category cell opens a menu and saves to the LOCAL
 * `expense.category` (`categorySource = manual`); "חסרה" carries a "סמן שיש" button; a foreign
 * purchase gets a grey chip and NO button (there is no Israeli VAT to reclaim).
 */

import type { ExpenseCategory, ExpenseRef, TransactionRow } from '../../../api-types.ts';
import { fmtMoney, fmtShortDate } from '../format.ts';
import { CATEGORY, SCOPE, he } from '../i18n/he.ts';
import { CategoryIcon, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';
import { Icon } from './Icon.tsx';
import { Chip, Mask, Money, Num } from './ui.tsx';
import { useApp } from '../hooks/useApp.tsx';

export function CategoryCell({ tx, onPick }: { tx: TransactionRow; onPick: (tx: TransactionRow, category: ExpenseCategory) => void }) {
  const { openMenu, closeLayer } = useApp();
  if (!tx.expense) return <Chip tone="outline" small>{SCOPE[tx.scope]}</Chip>;
  const current = tx.expense.category;
  return (
    <button
      type="button"
      className="cat-btn"
      aria-haspopup="menu"
      aria-label={`${he.cashflow.categoryMenu}: ${CATEGORY[current].label}`}
      onClick={(event) =>
        openMenu(
          event.currentTarget,
          <>
            <div className="hd">{he.cashflow.categoryMenu}</div>
            {(Object.keys(CATEGORY) as ExpenseCategory[]).map((key) => (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={key === current}
                onClick={() => {
                  closeLayer();
                  if (key !== current) onPick(tx, key);
                }}
              >
                <Icon name="check" className="chk" />
                {CATEGORY[key].label}
              </button>
            ))}
          </>,
          { label: he.cashflow.categoryMenu },
        )
      }
    >
      <CategoryIcon glyph={CATEGORY[current].glyph as GlyphName} size={16} />
      {CATEGORY[current].label}
      <Icon name="pencil" />
    </button>
  );
}

/**
 * The invoice state of ONE expense. Takes the `ExpenseRef` rather than a whole transaction so
 * both the transactions table and the expenses table can render it from their own row shape.
 */
export function InvoiceCell({ expense, onMark }: { expense: ExpenseRef | null; onMark: () => void }) {
  if (!expense) return <span className="note">{he.app.none}</span>;
  if (expense.foreign)
    return (
      <Chip tone="outline" icon="globe" small>
        {he.expenses.invoiceForeign}
      </Chip>
    );
  if (expense.hasTaxInvoice && expense.vatShare === 0)
    return (
      <Chip tone="outline" icon="check" small>
        {he.expenses.invoiceExempt}
      </Chip>
    );
  if (expense.hasTaxInvoice)
    return (
      <Chip tone="good" icon="check" small>
        {he.expenses.invoiceYes}
      </Chip>
    );
  return (
    <>
      <Chip tone="critical" icon="flag" small>
        {he.expenses.invoiceMissing}
      </Chip>{' '}
      <button type="button" className="btn xs" onClick={onMark}>
        {he.expenses.markHasInvoice}
      </button>
    </>
  );
}

export function MatchCell({ tx, onMatch }: { tx: TransactionRow; onMatch: (tx: TransactionRow) => void }) {
  if ((tx.amountAgorot ?? 0) <= 0) return <span className="note">{he.app.none}</span>;
  if (tx.match)
    return (
      <Chip tone="good" icon="check" small>
        {he.invoices.matchedInvoicesLabel}
        <span className="ltr">{tx.match.invoiceNumbers.map((n) => n.replace('2026-', '')).join(', ')}</span>
      </Chip>
    );
  return (
    <>
      <Chip tone="outline" icon="help" small>
        {he.invoices.notMatched}
      </Chip>{' '}
      <button type="button" className="btn xs primary" onClick={() => onMatch(tx)}>
        {he.cashflow.matchAction}
      </button>
    </>
  );
}

export function PendingChip() {
  return (
    <Chip tone="warn" icon="clock" small>
      {he.cashflow.pending}
    </Chip>
  );
}

/** The mobile list row (spec §4.2 "רשימת תנועות"). */
export function TxListItem({ tx, asOf }: { tx: TransactionRow; asOf: string }) {
  const label = tx.expense ? CATEGORY[tx.expense.category].label : SCOPE[tx.scope];
  return (
    <div className="list-item" role="listitem">
      <span className="li-ic" aria-hidden="true">
        {tx.kind === 'CARD' || tx.account.type === 'CARD' ? (
          <ProviderMark providerId={tx.account.providerId} size={28} />
        ) : tx.expense ? (
          <CategoryIcon glyph={CATEGORY[tx.expense.category].glyph as GlyphName} soft size={18} />
        ) : (
          <ProviderMark providerId={tx.account.providerId} size={28} />
        )}
      </span>
      <div className="li-main">
        <div className="li-t">
          {tx.merchant ?? tx.description}
          {tx.status === 'PENDING' ? <PendingChip /> : null}
        </div>
        <div className="li-s">
          <Num>{fmtShortDate(tx.effDate, asOf)}</Num> ·{' '}
          {tx.kind === 'CARD' ? <Mask>{`****${tx.account.numberLast4 ?? ''}`}</Mask> : he.cashflow.filterChecking} · {label}
          {(tx.amountAgorot ?? 0) > 0 ? (
            <>
              {' · '}
              {tx.match ? (
                <Chip tone="good" icon="check" small>
                  {he.invoices.matchedShort}
                </Chip>
              ) : (
                <Chip tone="outline" icon="help" small>
                  {he.invoices.notMatched}
                </Chip>
              )}
            </>
          ) : null}
        </div>
      </div>
      <div className="li-end">
        <span className="li-amt">
          <Money agorot={tx.amountAgorot} plus />
        </span>
      </div>
    </div>
  );
}

/** The VAT column of the transactions table. */
export function VatCell({ tx }: { tx: TransactionRow }) {
  if (!tx.expense) return <span className="note">{he.app.none}</span>;
  return <Money agorot={tx.expense.vatAgorot} decimals={2} />;
}

/** Accessible description of a transaction row, used by the mobile card's aria-label. */
export function txAria(tx: TransactionRow, asOf: string): string {
  return `${fmtShortDate(tx.effDate, asOf)} ${tx.merchant ?? tx.description} ${fmtMoney(tx.amountAgorot, 0, true)}`;
}
